import { PropsWithChildren, Suspense, useMemo, useState } from 'react';
import { useLocation, useMatches } from 'react-router-dom';
import { Box, Stack } from '@mui/material';
import Grid from '@mui/material/Grid';
import Logo from 'components/common/Logo';
import DefaultLoader from 'components/loading/DefaultLoader';
import AuthHeroContext, { AuthHeroOverride, HERO_PENDING } from './AuthHeroContext';
import { DEFAULT_AUTH_IMAGE } from './authHero';

const DefaultAuthLayout = ({ children }: PropsWithChildren) => {
  const { pathname } = useLocation();
  const isJoinPage = /\/wedding\/[^/]+\/join/.test(pathname);

  // Let the matched route choose its own hero (deepest match wins), falling
  // back to the shared default for every auth page that doesn't set one.
  const matches = useMatches();
  const routeAuthImage =
    [...matches]
      .reverse()
      .map((m) => (m.handle as { authImage?: string } | undefined)?.authImage)
      .find(Boolean) ?? DEFAULT_AUTH_IMAGE;

  // Runtime override for pages that only learn which hero to show after a fetch
  // (e.g. the guest join page resolves its eventType from the API). The join
  // page starts PENDING so the default hero never flashes before the resolved
  // one; other auth pages start unset and behave exactly as before.
  const [heroOverride, setHeroOverride] = useState<AuthHeroOverride>(
    isJoinPage ? HERO_PENDING : null,
  );
  const heroControls = useMemo(() => ({ setHeroImage: setHeroOverride }), []);
  const isHeroPending = heroOverride === HERO_PENDING;
  const overrideImage = typeof heroOverride === 'string' ? heroOverride : routeAuthImage;

  // A hero can be a presigned S3 URL (the couple's custom join-page photo), which
  // expires. If it ever fails to load we fall back to the bundled default rather
  // than showing a broken image on the first screen a guest sees. Guarded so the
  // default failing can't loop.
  const [brokenHeroSrc, setBrokenHeroSrc] = useState<string | null>(null);
  const authImage = overrideImage === brokenHeroSrc ? DEFAULT_AUTH_IMAGE : overrideImage;

  return (
    <AuthHeroContext.Provider value={heroControls}>
      <Grid
        container
        sx={{
          height: { lg: '100vh' },
          minHeight: '100vh',
          flexDirection: {
            xs: 'column',
            lg: 'row',
          },
        }}
      >
        <Grid
          sx={{
            position: 'relative',
            overflow: 'hidden',
            height: { xs: '300px', lg: '100%' },
            flexShrink: 0,
            // Neutral backdrop behind the photo — what shows during the brief
            // PENDING window on the join page (and behind the fade-in reveal).
            // White blends with the light hero photos for a soft reveal.
            ...(isJoinPage && { bgcolor: 'common.white' }),
          }}
          size={{
            xs: 12,
            lg: 6,
          }}
        >
          {!isHeroPending && (
            <Box
              key={authImage}
              component="img"
              src={authImage}
              alt="Agapaé"
              onError={() => {
                if (authImage !== DEFAULT_AUTH_IMAGE) setBrokenHeroSrc(authImage);
              }}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                // Gentle reveal so the resolved hero eases in over the backdrop
                // instead of popping. Join page only; other pages are unchanged.
                ...(isJoinPage && {
                  '@keyframes authHeroFadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
                  animation: 'authHeroFadeIn 600ms ease',
                }),
              }}
            />
          )}
          <Stack
            direction="column"
            sx={{
              position: 'relative',
              zIndex: 1,
              height: 1,
              p: { xs: 3, sm: 5 },
            }}
          >
            <Stack
              sx={{
                width: 1,
                justifyContent: { xs: 'center', lg: 'flex-start' },
              }}
            >
              <Logo
                variant={isHeroPending ? 'color' : 'white'}
                href={isJoinPage ? '#' : undefined}
              />
            </Stack>
          </Stack>
        </Grid>
        <Grid
          size={{
            lg: 6,
            xs: 12,
          }}
          sx={{
            display: { xs: 'flex', lg: 'block' },
            flexDirection: 'column',
            flex: 1,
            height: { lg: '100%' },
            overflowY: { lg: 'auto' },
          }}
        >
          <Suspense fallback={<DefaultLoader />}>{children}</Suspense>
        </Grid>
      </Grid>
    </AuthHeroContext.Provider>
  );
};

export default DefaultAuthLayout;
